"use client";
import {
  Box,
  Button,
  InputAdornment,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import React, { useState } from "react";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import toast from "react-hot-toast";
import { Loader } from "../genericComponents/MainLoader/Loader";
import { ApiService } from "../../services/AppService";
import { ResponseObject } from "@/app/classes/main";

const StyledTextField = styled(TextField)({
  "& .MuiInputAdornment-positionStart": {
    marginRight: "5px",
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: "14px",
    "&.Mui-focused fieldset": {
      borderColor: "#1f7a4d",
    },
  },
  "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
    display: "none",
  },
  "& input[type=number]": {
    MozAppearance: "textfield",
  },
});

const Enquire = () => {
  const [values, setValues] = useState({
    name: "",
    mobile: "",
    city: "",
  });
  const [errors, setErrors] = useState({
    name: "",
    mobile: "",
    city: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const apiService = new ApiService();

  // Handle input changes
  const handleDetails = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setValues((prevState) => ({ ...prevState, [name]: value }));
    setErrors((prevState) => ({ ...prevState, [name]: "" })); // Clear error when typing
  };

  // Validation function
  const validate = () => {
    const newErrors = {
      name: "",
      mobile: "",
      city: "",
    };
    let isValid = true;

    if (!values.name.trim()) {
      newErrors.name = "Name is required";
      isValid = false;
    }
    if (!values.mobile.trim()) {
      newErrors.mobile = "Mobile number is required";
      isValid = false;
    } else if (!/^\d{10}$/.test(values.mobile.trim())) {
      newErrors.mobile = "Enter a valid 10-digit mobile number";
      isValid = false;
    }
    if (!values.city.trim()) {
      newErrors.city = "City is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Submit handler
  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    const payload = {
      name: values.name.trim(),
      mobile: values.mobile.trim(),
      city: values.city.trim(),
    };

    setIsLoading(true);

    try {
      const responseObject: ResponseObject | undefined =
        await apiService.post<ResponseObject>(
          apiService.saveFranchiseDetails,
          payload
        );
      if (responseObject?.status === "success") {
        setMessage(responseObject?.message || "Form submitted successfully");
        toast.success("Form submitted successfully");
        setValues({
          name: "",
          mobile: "",
          city: "",
        });
      } else {
        toast.error("Something went wrong while submitting the form");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("An unexpected error occurred. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {isLoading && <Loader />}
      <Box
        sx={{
          display: { xs: "block", sm: "grid" },
          width: "100%",
          margin: "auto",
          borderRadius: "16px",
          boxShadow: "0 12px 40px rgba(18, 53, 36, 0.12)",
          padding: { xs: "1.25rem", sm: "1.5rem" },
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(246,251,248,0.92) 100%)",
          border: "1px solid rgba(18, 53, 36, 0.1)",
        }}
      >
        <Box sx={{ margin: "0.5rem auto 1rem" }}>
          <Typography
            variant="h5"
            component="h2"
            sx={{
              fontWeight: 700,
              textAlign: "center",
              color: "#123524",
              fontSize: { xs: "1.1rem", sm: "1.35rem" },
              lineHeight: 1.45,
            }}
          >
            Get a{" "}
            <Box component="span" sx={{ color: "#1f7a4d" }}>
              call back
            </Box>{" "}
            from our franchise team
          </Typography>
        </Box>
        <Box
          sx={{
            display: { xs: "block", sm: "block", md: "flex" },
            justifyContent: "space-evenly",
            alignItems: "center",
            margin: "auto",
            width: "100%",
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", sm: "100%", md: "100%" },
              padding: { xs: "0px", sm: "0px", md: "1rem" },
              margin: "auto",
            }}
          >
            {/* Name Field */}
            <StyledTextField
              label="Full Name"
              name="name"
              type="text"
              value={values.name}
              onChange={handleDetails}
              error={!!errors.name}
              helperText={errors.name}
              sx={{ margin: "0.5rem auto", width: "100%" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccountCircleIcon style={{ color: "#1f7a4d" }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* Mobile Field */}
            <StyledTextField
              label="Mobile Number"
              name="mobile"
              type="number"
              value={values.mobile}
              onChange={handleDetails}
              error={!!errors.mobile}
              helperText={errors.mobile}
              sx={{ margin: "0.5rem auto", width: "100%" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocalPhoneIcon style={{ color: "#1f7a4d" }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* City Field */}
            <StyledTextField
              label="City"
              name="city"
              type="text"
              value={values.city}
              onChange={handleDetails}
              error={!!errors.city}
              helperText={errors.city}
              sx={{ margin: "0.5rem auto", width: "100%" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationCityIcon style={{ color: "#1f7a4d" }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* Success Message */}
            {message && (
              <Typography
                variant="body1"
                sx={{ margin: "1rem auto", color: "#1f7a4d" }}
              >
                {message}
              </Typography>
            )}

            {/* Submit Button */}
            <Box>
              <Button
                variant="contained"
                sx={{
                  background: "linear-gradient(135deg, #123524 0%, #1f7a4d 100%)",
                  borderRadius: "999px",
                  margin: "2rem 0px 0rem",
                  width: "100%",
                  py: 1.25,
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "1rem",
                  boxShadow: "0 8px 24px rgba(18, 53, 36, 0.25)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #0f2d1f 0%, #1a6b42 100%)",
                  },
                }}
                onClick={handleSubmit}
              >
                Submit enquiry
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Enquire;
